import { useClientPortalAPI } from "@certn/api";
import { showToast } from "@certn/cascadia";
import {
  CheckboxControl,
  Container,
  DataTable,
  GhostButton,
  Layout,
  Pagination,
  Picker,
  PrimaryButton,
  Search,
  SecondaryTopNavWithStepper,
  SectionMessage,
  Stack,
  Text,
  type ColumnDefinition,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Checkbox as AriaCheckbox,
  type Key,
  type Selection,
} from "react-aria-components";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useQueryParams } from "../../../hooks/useQueryParams";
import { groupEligibleChecksByType } from "../enrollCheckTypeSummary";
import type { EligibleMonitoringApplicant } from "../types";
import {
  getApplicantCheckNames,
  getApplicantDisplayName,
  getApplicantGroupNames,
} from "../types";
import {
  useCreateEnrollments,
  useDashboardSummary,
  useEligibleApplicants,
  useRefreshDashboardApplicants,
  useRefreshEligibleApplicants,
  useRefreshMonitoringApplicantDetails,
} from "../useMonitoringApi";
import { useCanEnrollInMonitoring } from "../useCanEnrollInMonitoring";
import { checkRowSpaceBetweenStyle } from "@certn/monitoring";

const MONITORING_PATH = "/monitoring";
const DEFAULT_PAGE_SIZE = "25";

type EnrollStep = "select-applicants" | "select-checks";

function defaultEligibleCheckCreatedAfter(): string {
  const date = new Date();
  date.setMonth(date.getMonth() - 12);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function EnrollApplicantsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canEnroll = useCanEnrollInMonitoring();
  const refreshEligible = useRefreshEligibleApplicants();
  const refreshApplicantDetails = useRefreshMonitoringApplicantDetails();
  const refreshDashboard = useRefreshDashboardApplicants();
  const { params, setParam, clearParam, setParams } = useQueryParams();

  const [step, setStep] = useState<EnrollStep>("select-applicants");
  const [selectedApplicants, setSelectedApplicants] = useState<
    EligibleMonitoringApplicant[]
  >([]);
  const [selectedCheckIds, setSelectedCheckIds] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    if (!params.eligible_check_created_after) {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(
            "eligible_check_created_after",
            defaultEligibleCheckCreatedAfter()
          );
          return next;
        },
        { replace: true }
      );
    }
  }, [params.eligible_check_created_after, setParams]);

  const selectionResetKey = useMemo(
    () =>
      [
        params.search,
        params.check_type,
        params.customer_group_id,
        params.eligible_check_created_after,
        params.eligible_check_created_before,
        params.page,
        params.page_size,
      ].join("|"),
    [params]
  );
  const selectionResetKeyRef = useRef(selectionResetKey);
  useEffect(() => {
    if (selectionResetKeyRef.current === selectionResetKey) {
      return;
    }
    selectionResetKeyRef.current = selectionResetKey;
    setSelectedApplicants([]);
  }, [selectionResetKey]);

  const apiParams = useMemo(() => {
    const query: Record<string, string | string[]> = {
      page: params.page || "1",
      page_size: params.page_size || DEFAULT_PAGE_SIZE,
    };
    if (params.search) {
      query.search = params.search;
    }
    if (params.check_type) {
      query.check_type = params.check_type.split(",");
    }
    if (params.customer_group_id) {
      query.customer_group_id = params.customer_group_id.split(",");
    }
    if (params.eligible_check_created_after) {
      query.eligible_check_created_after = params.eligible_check_created_after;
    }
    if (params.eligible_check_created_before) {
      query.eligible_check_created_before =
        params.eligible_check_created_before;
    }
    return query;
  }, [params]);

  const eligibleQueryEnabled =
    canEnroll && Boolean(params.eligible_check_created_after);
  const { data, isLoading } = useEligibleApplicants(
    eligibleQueryEnabled ? apiParams : null
  );
  const applicants: EligibleMonitoringApplicant[] = data?.results ?? [];

  const { submit, isSubmitting } = useCreateEnrollments({
    onSuccess: async () => {
      void Promise.all([
        refreshEligible(),
        refreshApplicantDetails(),
        refreshDashboard(),
      ]).catch(() => {});
      showToast(t("Applicants enrolled for monitoring."), { type: "success" });
      navigate(MONITORING_PATH);
    },
  });

  function toggleChecks(checkIds: string[], shouldSelect: boolean) {
    setSelectedCheckIds((prev) => {
      const next = new Set(prev);
      checkIds.forEach((id) => (shouldSelect ? next.add(id) : next.delete(id)));
      return next;
    });
  }

  function handleNext() {
    const allChecks = selectedApplicants.flatMap(
      (a) => a.eligible_monitoring_checks
    );
    setSelectedCheckIds(new Set(allChecks.map((c) => c.id)));
    setStep("select-checks");
  }

  function handleBack() {
    setStep("select-applicants");
    setSelectedCheckIds(new Set());
  }

  function handleCancel() {
    navigate(MONITORING_PATH);
  }

  async function handleConfirm() {
    if (selectedCheckIds.size === 0) return;
    await submit({ check_ids: Array.from(selectedCheckIds) });
  }

  const monitoredApplicantCount = selectedApplicants.filter((applicant) =>
    applicant.eligible_monitoring_checks.some((check) =>
      selectedCheckIds.has(check.id)
    )
  ).length;

  const currentStepIndex = step === "select-applicants" ? 0 : 1;
  const steps = [t("Select a person"), t("Select a check")].map(
    (title, index) => ({
      title,
      status:
        index < currentStepIndex
          ? ("COMPLETE" as const)
          : ("INCOMPLETE" as const),
    })
  );

  return (
    <Layout
      layout="narrow"
      backgroundColor="surface-base"
      header={
        <SecondaryTopNavWithStepper
          testID="enroll-applicants-top-nav"
          stepperSteps={steps}
          currentStepIndex={currentStepIndex}
          handleClose={handleCancel}
          handlePrevStep={handleBack}
        >
          <GhostButton onPress={handleCancel} testID="enroll-cancel-button">
            {t("Cancel")}
          </GhostButton>
          {step === "select-applicants" ? (
            <PrimaryButton
              isDisabled={selectedApplicants.length === 0}
              onPress={handleNext}
              testID="enroll-next-button"
            >
              {t("Continue")}
            </PrimaryButton>
          ) : (
            <PrimaryButton
              isDisabled={selectedCheckIds.size === 0 || isSubmitting}
              isLoading={isSubmitting}
              onPress={handleConfirm}
              testID="enroll-confirm-button"
            >
              {t("Start monitoring ({{count}})", {
                count: monitoredApplicantCount,
              })}
            </PrimaryButton>
          )}
        </SecondaryTopNavWithStepper>
      }
    >
      <Container layout="wide" maxWidth={880}>
        {step === "select-applicants" ? (
          <SelectPersonStep
            applicants={applicants}
            isLoading={!eligibleQueryEnabled || isLoading}
            totalCount={data?.count ?? 0}
            selectedApplicants={selectedApplicants}
            setSelectedApplicants={setSelectedApplicants}
            params={params}
            setParam={setParam}
            clearParam={clearParam}
            setParams={setParams}
          />
        ) : (
          <Stack gap="space-4">
            <Stack gap="space-1">
              <Text style="global/title-medium">{t("Select a check")}</Text>
              <Text style="form/body-secondary">
                {t(
                  "Only the checks an applicant is eligible for will be applied to them."
                )}
              </Text>
            </Stack>
            <SectionMessage
              type="warning"
              icon="warning"
              testID="enroll-cost-warning"
            >
              {t(
                "Please note, this action will enrol the applicant in continuous monitoring and will incur a subscription charge per applicant for each of the selected check."
              )}
            </SectionMessage>
            <CheckSelectionStep
              applicants={selectedApplicants}
              selectedCheckIds={selectedCheckIds}
              onToggleChecks={toggleChecks}
            />
          </Stack>
        )}
      </Container>
    </Layout>
  );
}

function SelectPersonStep({
  applicants,
  isLoading,
  totalCount,
  selectedApplicants,
  setSelectedApplicants,
  params,
  setParam,
  clearParam,
  setParams,
}: {
  applicants: EligibleMonitoringApplicant[];
  isLoading: boolean;
  totalCount: number;
  selectedApplicants: EligibleMonitoringApplicant[];
  setSelectedApplicants: React.Dispatch<
    React.SetStateAction<EligibleMonitoringApplicant[]>
  >;
  params: Record<string, string>;
  setParam: (param: string, value?: string | string[] | Key[]) => void;
  clearParam: (param: string) => void;
  setParams: ReturnType<typeof useQueryParams>["setParams"];
}) {
  const { t } = useTranslation();
  const { data: summary } = useDashboardSummary();
  const { data: customerGroups, isLoading: groupsLoading } = useClientPortalAPI(
    "/api/internal/client-portal/customer-accounts/customer-groups/"
  );

  const checkOptions = useMemo(
    () =>
      (summary?.monitorable_check_types ?? []).map((option) => ({
        id: option.type,
        name: option.label || option.type,
      })),
    [summary?.monitorable_check_types]
  );

  const selectedKeys = useMemo<Set<Key>>(
    () => new Set(selectedApplicants.map((a) => a.id)),
    [selectedApplicants]
  );

  const pageSize = Number(params.page_size || DEFAULT_PAGE_SIZE);
  const page = Number(params.page || "1");
  const committedSearchRef = useRef(params.search ?? "");
  const [searchInput, setSearchInput] = useState(params.search ?? "");

  useEffect(() => {
    setSearchInput(params.search ?? "");
    committedSearchRef.current = params.search ?? "";
  }, [params.search]);

  const handleSearchChange = useCallback(
    (value: string) => {
      if (committedSearchRef.current === value) {
        return;
      }
      committedSearchRef.current = value;
      setSearchInput(value);
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("page");
          if (value) {
            next.set("search", value);
          } else {
            next.delete("search");
          }
          return next;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  function handleSelectionChange(keys: Selection) {
    if (keys === "all") {
      setSelectedApplicants(applicants);
      return;
    }
    const selectedIds = new Set(Array.from(keys).map(String));
    const byId = new Map(applicants.map((a) => [a.id, a]));
    setSelectedApplicants(
      Array.from(selectedIds)
        .map((id) => byId.get(id))
        .filter((a): a is EligibleMonitoringApplicant => Boolean(a))
    );
  }

  const columns: ColumnDefinition<EligibleMonitoringApplicant>[] = [
    {
      key: "name",
      name: t("Name"),
      isRowHeader: true,
      render: (a) => (
        <Stack gap="space-0">
          <Text style="form/body-secondary">{getApplicantDisplayName(a)}</Text>
          {a.latest_email && (
            <Text style="global/info-label">{a.latest_email}</Text>
          )}
        </Stack>
      ),
    },
    {
      key: "checks",
      name: t("Check eligibility"),
      render: (a) => (
        <Stack gap="space-0">
          {getApplicantCheckNames(a).map((name) => (
            <Text key={name} style="form/body-secondary">
              {name}
            </Text>
          ))}
        </Stack>
      ),
    },
    {
      key: "group",
      name: t("Group"),
      render: (a) => (
        <Stack gap="space-0">
          {getApplicantGroupNames(a).map((name) => (
            <Text key={name} style="form/body-secondary">
              {name}
            </Text>
          ))}
        </Stack>
      ),
    },
  ];

  return (
    <Stack gap="space-4">
      <Stack gap="space-1">
        <Text style="global/title-medium">{t("Select a person")}</Text>
        <Text style="form/body-secondary">
          {t(
            "Choose the person you want to start monitoring. Anyone with a paused or deactivated monitoring status does not appear in this list."
          )}
        </Text>
      </Stack>

      <Stack
        orientation="horizontal"
        gap="space-2"
        justify="space-between"
        align="center"
        className={styles.filters}
      >
        <Stack orientation="horizontal" gap="space-2" align="center">
          {checkOptions.length > 0 && (
            <Picker
              testID="enroll-check-filter"
              label={t("Check eligibility")}
              selectionMode="multiple"
              showActionBar
              selectedKeys={params.check_type?.split(",") || []}
              items={checkOptions}
              onSelectionChange={(keys) => {
                clearParam("page");
                setParam(
                  "check_type",
                  keys === "all"
                    ? checkOptions.map((option) => option.id)
                    : Array.from(keys).map(String)
                );
              }}
            />
          )}
          <Picker
            testID="enroll-group-filter"
            label={t("Group")}
            selectionMode="multiple"
            showActionBar
            isLoading={groupsLoading}
            selectedKeys={params.customer_group_id?.split(",") || []}
            items={customerGroups ?? []}
            onSelectionChange={(keys) => {
              clearParam("page");
              setParam("customer_group_id", Array.from(keys).map(String));
            }}
          />
        </Stack>
        <div className={styles.search}>
          <Search
            testID="enroll-search"
            aria-label={t("Search by name or email")}
            placeholder={t("Search by name or email…")}
            debounce={300}
            value={searchInput}
            onChange={handleSearchChange}
          />
        </div>
      </Stack>

      <DataTable
        testId="enroll-applicants-table"
        aria-label={t("Eligible applicants")}
        columns={columns}
        rows={applicants}
        rowKey="id"
        isLoading={isLoading}
        minWidth="100%"
        disableScrolling="visible"
        selectionMode="multiple"
        selectionBehavior="toggle"
        selectedKeys={selectedKeys}
        onSelectionChange={handleSelectionChange}
        renderEmptyState={() => (
          <Text style="form/body-secondary">
            {t("No eligible applicants found.")}
          </Text>
        )}
      />

      {totalCount > 0 && (
        <Pagination
          testID="enroll-applicants-pagination"
          page={page}
          pageCount={pageSize}
          total={totalCount}
          setPage={(nextPage) => setParam("page", String(nextPage))}
          setPageCount={(nextPageSize) =>
            setParam("page_size", String(nextPageSize))
          }
        />
      )}
    </Stack>
  );
}

function CheckSelectionStep({
  applicants,
  selectedCheckIds,
  onToggleChecks,
}: {
  applicants: EligibleMonitoringApplicant[];
  selectedCheckIds: Set<string>;
  onToggleChecks: (checkIds: string[], shouldSelect: boolean) => void;
}) {
  const { t } = useTranslation();
  const checkGroups = useMemo(
    () => groupEligibleChecksByType(applicants),
    [applicants]
  );

  if (checkGroups.length === 0) {
    return (
      <Text style="form/body-secondary">
        {t("No eligible checks for the selected applicants.")}
      </Text>
    );
  }

  return (
    <Stack gap="space-0">
      {checkGroups.map((group) => {
        const isSelected = group.checkIds.every((id) =>
          selectedCheckIds.has(id)
        );
        return (
          <CheckTypeRow
            key={group.name}
            name={group.name}
            applicantCount={group.applicantCount}
            isSelected={isSelected}
            onToggle={() => onToggleChecks(group.checkIds, !isSelected)}
          />
        );
      })}
    </Stack>
  );
}

function CheckTypeRow({
  name,
  applicantCount,
  isSelected,
  onToggle,
}: {
  name: string;
  applicantCount: number;
  isSelected: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  return (
    <AriaCheckbox
      isSelected={isSelected}
      onChange={onToggle}
      className={checkRowSpaceBetweenStyle}
      data-testid={`enroll-check-${name}`}
    >
      {({ isSelected: selected, isHovered, isFocusVisible }) => (
        <>
          <Stack gap="space-0">
            <Text style="form/body-secondary">
              {t("{{checkType}} ({{count}})", {
                checkType: name,
                count: applicantCount,
              })}
            </Text>
          </Stack>
          <CheckboxControl
            isSelected={selected}
            isHovered={isHovered}
            isFocusVisible={isFocusVisible}
          />
        </>
      )}
    </AriaCheckbox>
  );
}

const styles = {
  filters: css({
    flexWrap: "wrap",
  }),
  search: css({
    width: "276px",
    maxWidth: "100%",
  }),
};
